import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHead, errMsg } from "@/components/tm/TmParts";

const EMPTY = { priorities: [{ title: "", expected_output: "", task_id: "", eta: "" }], carried_forward: [], blockers: "", dependencies: "" };

export default function Standup() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const r = await api.get("/tm/standup/today");
    setD(r.data);
    if (r.data.report) setF({ ...EMPTY, ...r.data.report, priorities: r.data.report.priorities?.length ? r.data.report.priorities : EMPTY.priorities });
    setHist((await api.get("/tm/reports-daily/mine", { params: { kind: "standup" } })).data);
  };
  useEffect(() => { load().catch(() => {}); }, []);
  const setP = (i, k, v) => setF((p) => ({ ...p, priorities: p.priorities.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const save = async (submit) => {
    const prios = f.priorities.filter((p) => p.title.trim());
    if (submit && !prios.length) return toast.error("Add at least one priority for today");
    setBusy(true);
    try {
      await api.post("/tm/standup", { ...f, priorities: prios, submit });
      toast.success(submit ? "Stand-up submitted" : "Draft saved");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-sm text-muted-foreground" data-testid="standup-loading">Loading…</p>;
  const submitted = d.report?.status === "submitted";
  return (
    <div className="space-y-5" data-testid="standup-page">
      <PageHead title="Daily Stand-up" sub={`${d.date} · due by ${d.settings.standup_deadline} IST`} right={submitted && <span className="tm-chip tm-done" data-testid="standup-submitted">Submitted {new Date(d.report.submitted_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>} />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-soft p-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold">Today's top priorities &amp; expected output</Label>
            {f.priorities.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mt-2">
                <Input className="col-span-12 sm:col-span-5" placeholder={`Priority ${i + 1}`} value={p.title} disabled={submitted} onChange={(e) => setP(i, "title", e.target.value)} data-testid={`su-prio-${i}`} />
                <Input className="col-span-8 sm:col-span-4" placeholder="Expected output" value={p.expected_output} disabled={submitted} onChange={(e) => setP(i, "expected_output", e.target.value)} data-testid={`su-output-${i}`} />
                <select className="col-span-4 sm:col-span-2 h-9 rounded-md border border-input bg-background px-1 text-xs" value={p.task_id || ""} disabled={submitted} onChange={(e) => setP(i, "task_id", e.target.value)} data-testid={`su-task-${i}`}>
                  <option value="">Link task…</option>{d.open_tasks.map((t) => <option key={t.task_id} value={t.task_id}>{t.task_id} {t.title.slice(0, 24)}</option>)}
                </select>
                <Input type="time" className="col-span-12 sm:col-span-1 px-1" value={p.eta || ""} disabled={submitted} onChange={(e) => setP(i, "eta", e.target.value)} />
              </div>))}
            {!submitted && <Button size="sm" variant="ghost" className="mt-2" onClick={() => setF((p) => ({ ...p, priorities: [...p.priorities, { title: "", expected_output: "", task_id: "", eta: "" }] }))} data-testid="su-add-prio">+ Add priority</Button>}
          </div>
          <div>
            <Label className="text-xs font-semibold">Carried forward from yesterday</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="su-carry">
              {d.carry_candidates.length === 0 && <span className="text-xs text-muted-foreground">No overdue tasks to carry forward.</span>}
              {d.carry_candidates.map((t) => (
                <button key={t.task_id} type="button" disabled={submitted} onClick={() => setF((p) => ({ ...p, carried_forward: p.carried_forward.includes(t.task_id) ? p.carried_forward.filter((x) => x !== t.task_id) : [...p.carried_forward, t.task_id] }))}
                  className={`tm-chip ${f.carried_forward.includes(t.task_id) ? "tm-progress" : "tm-muted"}`}>{t.task_id} · {t.title.slice(0, 30)}</button>))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Blockers &amp; support needed</Label><Textarea rows={3} value={f.blockers || ""} disabled={submitted} onChange={(e) => setF({ ...f, blockers: e.target.value })} data-testid="su-blockers" /></div>
            <div><Label className="text-xs font-semibold">Dependencies on other departments</Label><Textarea rows={3} value={f.dependencies || ""} disabled={submitted} onChange={(e) => setF({ ...f, dependencies: e.target.value })} data-testid="su-deps" /></div>
          </div>
          {!submitted && (
            <div className="flex gap-2 justify-end">
              <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="su-save-draft">Save Draft</Button>
              <Button className="bg-primary text-white" disabled={busy} onClick={() => save(true)} data-testid="su-submit">Submit Stand-up</Button>
            </div>)}
          {(d.report?.comments || []).length > 0 && (
            <div className="border-t border-border pt-3 text-xs space-y-1" data-testid="su-comments">{d.report.comments.map((c, i) => <p key={i}><span className="font-semibold">{c.by}:</span> {c.text}{c.clarification && <span className="tm-chip tm-warn ml-1">clarification</span>}</p>)}</div>)}
        </div>
        <div className="card-soft p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Previous submissions</p>
          <div className="space-y-1.5 text-xs max-h-[420px] overflow-y-auto" data-testid="su-history">
            {hist.length === 0 && <p className="text-muted-foreground">No history yet.</p>}
            {hist.map((h) => <div key={h.report_id} className="flex items-center justify-between border-b border-border/60 pb-1"><span className="metric">{h.date}</span><span className={`tm-chip ${h.status === "submitted" ? "tm-done" : "tm-warn"}`}>{h.status}</span><span className="text-muted-foreground">{(h.priorities || []).length} priorities</span></div>)}
          </div>
        </div>
      </div>
    </div>);
}
